import { useEffect, useState } from 'react';
import { X, ArrowRight } from 'lucide-react';
import { api } from '../lib/api';
import { publicFileUrl } from '../lib/api';
import LivingImage from './living/LivingImage';

const resolveImg = (src) => {
    if (!src) return null;
    if (/^https?:\/\//i.test(src)) return src;
    return publicFileUrl(src);
};

// Renders founder-managed content blocks (banners + cards) for a given placement.
export default function AppContent({ placement = 'dashboard', className = '' }) {
    const [items, setItems] = useState([]);
    const [dismissed, setDismissed] = useState(() => {
        try { return JSON.parse(localStorage.getItem('gd_dismissed_content') || '[]'); } catch { return []; }
    });

    useEffect(() => {
        api.get('/app-content', { params: { placement } })
            .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
            .catch(() => setItems([]));
    }, [placement]);

    const dismiss = (id) => {
        const next = [...dismissed, id];
        setDismissed(next);
        try { localStorage.setItem('gd_dismissed_content', JSON.stringify(next)); } catch { /* ignore */ }
    };

    const visible = items.filter((i) => !dismissed.includes(i.id));
    if (!visible.length) return null;

    return (
        <div className={`space-y-3 ${className}`} data-testid={`app-content-${placement}`}>
            {visible.map((item) => {
                const img = resolveImg(item.image);
                const Wrapper = item.link_url ? 'a' : 'div';
                const wrapProps = item.link_url
                    ? { href: item.link_url, target: '_blank', rel: 'noreferrer' }
                    : {};

                if (item.type === 'banner') {
                    return (
                        <div
                            key={item.id}
                            data-testid={`content-banner-${item.id}`}
                            className="relative rounded-2xl px-4 py-3 flex items-center gap-3 overflow-hidden"
                            style={{ backgroundColor: item.bg_color || '#2C4C3B', color: item.text_color || '#fff' }}
                        >
                            {img && <img src={img} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />}
                            <div className="min-w-0 flex-1">
                                {item.title && <div className="font-semibold text-sm leading-tight">{item.title}</div>}
                                {item.body && <div className="text-xs opacity-90 mt-0.5">{item.body}</div>}
                            </div>
                            {item.link_url && (
                                <Wrapper {...wrapProps} className="text-xs font-bold underline whitespace-nowrap opacity-95 hover:opacity-100">
                                    {item.link_label || 'Open'}
                                </Wrapper>
                            )}
                            <button onClick={() => dismiss(item.id)} data-testid={`content-dismiss-${item.id}`} className="opacity-70 hover:opacity-100 flex-shrink-0" aria-label="Dismiss">
                                <X size={15} />
                            </button>
                        </div>
                    );
                }

                // card
                return (
                    <Wrapper
                        key={item.id}
                        {...wrapProps}
                        data-testid={`content-card-${item.id}`}
                        className="block rounded-3xl border border-brand-border overflow-hidden bg-brand-card hover:-translate-y-0.5 transition-transform"
                    >
                        {img && <LivingImage src={img} className="w-full h-36" />}
                        <div className="p-4">
                            {item.title && <h3 className="font-serif text-lg text-brand-ink">{item.title}</h3>}
                            {item.body && <p className="text-sm text-brand-ink2 mt-1 whitespace-pre-line">{item.body}</p>}
                            {item.link_url && (
                                <span className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-brand-green">
                                    {item.link_label || 'Learn more'} <ArrowRight size={14} />
                                </span>
                            )}
                        </div>
                    </Wrapper>
                );
            })}
        </div>
    );
}
